import { lazy } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { PageRoute } from 'src/routes/routes-behind-login/page-route'
import { ExactScopeName } from '~hooks/permissions/use-has-scope'
import { PageNotFound } from '~pages/open-pages/page-not-found'
import type { ExtendedRouteObject } from '../routes'

export enum PageAccess {
  EXTERNAL = 'external',
  INTERNAL = 'internal',
}

export const routesBehindLogin: ExtendedRouteObject[] = [
  {
    path: '*',
    Component: lazy(() => Promise.resolve({ default: () => <PageNotFound /> })),
    handle: { disableLeftNavigation: true, pageAccess: PageAccess.EXTERNAL },
  },
  {
    path: PageRoute.VERIFY_EMAIL,
    Component: lazy(
      () =>
        import('~pages/pages-behind-login/verify-email-page/verify-email-page'),
    ),
    handle: { disableLeftNavigation: true, pageAccess: PageAccess.EXTERNAL },
  },
  {
    path: PageRoute.PROFILE,
    Component: lazy(
      () => import('~pages/pages-behind-login/profile-page/profile-page'),
    ),
    handle: { disableLeftNavigation: true, pageAccess: PageAccess.EXTERNAL },
  },
  {
    path: `${PageRoute.FLEET}/vessels`,
    Component: lazy(
      () => import('../../pages/pages-behind-login/vessels-page/vessels-page'),
    ),
    handle: { disableLeftNavigation: true, pageAccess: PageAccess.EXTERNAL },
  },
  {
    path: PageRoute.PORT_INFO,
    Component: lazy(
      () =>
        import('../../pages/pages-behind-login/port-info-page/port-info-page'),
    ),
    handle: {
      disableLeftNavigation: true,
      pageAccess: PageAccess.INTERNAL,
      restrictedTo: [
        ExactScopeName.technicalManagement,
        ExactScopeName.portInfoRead,
        ExactScopeName.portInfoWrite,
      ],
    },
  },
  {
    path: PageRoute.ACCOUNT_MANAGER,
    Component: lazy(
      () => import('../../pages/pages-behind-login/manager/manager'),
    ),
    handle: { pageAccess: PageAccess.INTERNAL },
    children: [
      {
        path: 'users',
        Component: lazy(
          () => import('../../pages/pages-behind-login/users-page/users-page'),
        ),
        handle: {
          disableLeftNavigation: true,
          restrictedTo: [ExactScopeName.mtopsVessel, ExactScopeName.poolAdmin],
        },
      },
      {
        path: 'pool-partners',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/pool-partners-page/pool-partners-page'),
        ),
        handle: {
          disableLeftNavigation: true,
          restrictedTo: [ExactScopeName.mtopsVessel, ExactScopeName.poolAdmin],
        },
      },
    ],
  },
  {
    path: PageRoute.SYSTEM_SETTINGS,
    Component: lazy(
      () => import('../../pages/pages-behind-login/system-settings'),
    ),
    handle: { disableLeftNavigation: true, pageAccess: PageAccess.INTERNAL },
    children: [
      {
        path: 'error-stream',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/error-stream/error-stream-page'),
        ),
        handle: {
          disableLeftNavigation: true,
          restrictedTo: ['*:*:admin'],
        },
      },
      {
        path: 'welcome-popup',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/welcome-popup/welcome-popup-page'),
        ),
        handle: {
          disableLeftNavigation: true,
          restrictedTo: [ExactScopeName.welcomePopupWriter, 'settings:*:write'],
        },
      },
      {
        path: 'notification-banner',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/notification-banner/notification-banner-page'),
        ),
        handle: {
          disableLeftNavigation: true,
          restrictedTo: [ExactScopeName.notificationWriter, 'settings:*:write'],
        },
      },
    ],
  },
  {
    path: PageRoute.CHARTERING,
    Component: lazy(
      () => import('../../pages/pages-behind-login/chartering/chartering'),
    ),
    handle: {
      restrictedTo: [ExactScopeName.maerskTankersUser],
      disableLeftNavigation: true,
      pageAccess: PageAccess.INTERNAL,
    },
    children: [
      {
        path: 'position-list',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/chartering/position-list-page/position-list'),
        ),
      },
      {
        path: 'map',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/chartering/map-page/new-map'),
        ),
      },
      {
        path: 'exposure',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/chartering/exposure-page/exposure-page'),
        ),
      },
      {
        path: 'cargo-list',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/chartering/cargo-list-page/cargo-list-page'),
        ),
        handle: {
          disableLeftNavigation: true,
        },
      },
      {
        path: 'cargo-statistics',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/chartering/cargo-statistics-page/cargo-statistics-page'),
        ),
        handle: {
          disableLeftNavigation: true,
        },
      },
      {
        path: 'pre-fix',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/chartering/pre-fixture-page/pre-fixture-page'),
        ),
        handle: {
          disableLeftNavigation: true,
        },
      },
      {
        path: 'sanction-clauses',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/sanction-clauses/sanction-clauses-page'),
        ),
        handle: {
          disableLeftNavigation: true,
        },
      },
      {
        path: 'owner-clauses',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/owner-clauses/owner-clauses-page'),
        ),
        handle: {
          disableLeftNavigation: true,
        },
      },
      {
        path: 'clear',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/chartering/call-log-page/call-log-page'),
        ),
        handle: {
          disableLeftNavigation: true,
        },
      },
      {
        path: 'recap-hub',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/recap-hub-page/recap-hub-page'),
        ),
        handle: {
          disableLeftNavigation: true,
        },
      },
    ],
  },
  {
    path: PageRoute.OPERATIONS,
    Component: lazy(
      () => import('../../pages/pages-behind-login/operations/operations'),
    ),
    handle: {
      disableLeftNavigation: true,
    },
    children: [
      {
        path: 'incidents',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/operations/incidents-page/incidents'),
        ),
        handle: {
          restrictedTo: [ExactScopeName.incidentsWriter],
          pageAccess: PageAccess.INTERNAL,
        },
      },
      {
        path: 'cdi',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/operations/cdi-page/cdi'),
        ),
        handle: { pageAccess: PageAccess.EXTERNAL },
      },
      {
        path: 'sire',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/operations/sire-page/sire'),
        ),
        handle: { pageAccess: PageAccess.EXTERNAL },
      },
      {
        path: 'rejections',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/operations/rejections-page/rejections'),
        ),
        handle: { pageAccess: PageAccess.EXTERNAL },
      },
      {
        path: 'recap-hub',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/recap-hub-page/recap-hub-page'),
        ),
        handle: {
          pageAccess: PageAccess.INTERNAL,
        },
      },
      {
        path: 'vesseldata',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/operations/vesseldata-page/vesseldata'),
        ),
        handle: {
          restrictedTo: [ExactScopeName.maerskTankersUser],
          pageAccess: PageAccess.INTERNAL,
        },
      },
      {
        path: 'hull-operations',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/operations/hull-operations-page/hull-operations'),
        ),
        handle: {
          restrictedTo: [ExactScopeName.maerskTankersUser],
          pageAccess: PageAccess.INTERNAL,
        },
      },
      {
        path: 'dry-dock',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/operations/dry-dock-page/dry-dock'),
        ),
        handle: {
          restrictedTo: [ExactScopeName.dryDockWriter],
          pageAccess: PageAccess.INTERNAL,
        },
      },
      {
        path: 'vessel-profile',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/operations/vessel-profile-page/vessel-profile'),
        ),
        handle: {
          restrictedTo: [ExactScopeName.vesselProfileWriter],
          pageAccess: PageAccess.INTERNAL,
        },
      },
    ],
  },
  {
    path: 'fleet/vessels/:vesselId/*',
    Component: lazy(
      () =>
        import('../../pages/pages-behind-login/vessel-page/vessel-page-wrapper'),
    ),
    handle: { withHamburger: true, leftNavigationPage: 'vessels' },
    children: [
      {
        path: 'vessel-dashboard',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/onboarded-vessel/vessel-dashboard-page/vessel-dashboard-page'),
        ),
        handle: {
          id: 'vesseldashboard',
          pageAccess: PageAccess.INTERNAL,
        },
      },
      {
        path: 'ops-dashboard',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/onboarded-vessel/ops-dashboard-page/ops-dashboard-page'),
        ),
        handle: {
          id: 'opsdashboard',
          pageAccess: PageAccess.INTERNAL,
        },
      },
      {
        path: 'basic-vessel-data',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/onboarded-vessel/basic-vessel-data-page/basic-vessel-data-page'),
        ),
        handle: {
          id: 'basicvesseldata',
          pageAccess: PageAccess.EXTERNAL,
        },
      },
      {
        path: 'documents',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/onboarded-vessel/documents-page/documents-page'),
        ),
        handle: {
          id: 'certificates',
          pageAccess: PageAccess.EXTERNAL,
        },
      },
      {
        path: 'questionnaire',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/onboarded-vessel/questionnaire-page-v2/questionnaire-page-v2'),
        ),
        handle: {
          id: 'questionnaire',
          pageAccess: PageAccess.EXTERNAL,
        },
      },
      {
        path: 'partner-dashboard',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/onboarded-vessel/partner-dashboard-page/partner-dashboard-page'),
        ),
        handle: {
          id: 'poolpartnerdashboard',
          pageAccess: PageAccess.EXTERNAL,
        },
      },
      {
        path: 'vetting',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/onboarded-vessel/vetting/vetting-page'),
        ),
        handle: {
          id: 'vetting',
          pageAccess: PageAccess.EXTERNAL,
        },
      },
      {
        path: 'voyages',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/onboarded-vessel/voyages-page/voyages-page'),
        ),
        handle: {
          id: 'voyages',
          pageAccess: PageAccess.EXTERNAL,
        },
      },
      {
        path: 'pool-points',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/onboarded-vessel/pool-points-page/pool-points-page'),
        ),
        handle: {
          id: 'poolpoints',
          pageAccess: PageAccess.INTERNAL,
        },
      },
    ],
  },
  {
    path: 'dashboard/:pool',
    Component: lazy(
      () => import('../../pages/pages-behind-login/dashboard/v2/dashboard'),
    ),
    handle: {
      disableLeftNavigation: true,
      pageAccess: PageAccess.EXTERNAL,
    },
  },
  {
    path: 'help',
    Component: lazy(() => Promise.resolve({ default: () => <Outlet /> })),
    handle: {
      disableLeftNavigation: true,
      pageAccess: PageAccess.EXTERNAL,
    },
    children: [
      {
        index: true,
        Component: lazy(
          () => import('../../pages/pages-behind-login/help/help-home'),
        ),
      },
      {
        path: 'emissions',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/help/sub-pages/help-emissions'),
        ),
      },
      {
        path: 'bunker-adjustment',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/help/sub-pages/help-bunker-adjustment'),
        ),
      },
      {
        path: 'finance-and-reporting',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/help/sub-pages/help-finance-and-reporting'),
        ),
      },
      {
        path: 'other',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/help/sub-pages/help-other'),
        ),
      },
      {
        path: 'search/:searchQuery',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/help/sub-pages/search-results'),
        ),
      },
      {
        path: 'inputs',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/help-inputs/help-inputs-page'),
        ),
        handle: {
          pageAccess: PageAccess.INTERNAL,
          restrictedTo: ['settings:help:write', 'settings:*:write'],
        },
      },
    ],
  },
  {
    path: 'document',
    Component: lazy(() => import('../../pages/pages-behind-login/document')),
    handle: {
      restrictedTo: [
        new RegExp(/poolpartner:\w*:user$/),
        ExactScopeName.maerskTankersUser,
      ],
      disableLeftNavigation: true,
      pageAccess: PageAccess.EXTERNAL,
    },
  },
  {
    path: 'documents/:pool',
    Component: lazy(
      () => import('../../pages/pages-behind-login/documents/documents'),
    ),
    handle: {
      withPools: true,
      restrictedTo: [
        new RegExp(/poolpartner:\w*:user$/),
        ExactScopeName.maerskTankersUser,
      ],
      disableLeftNavigation: true,
      pageAccess: PageAccess.EXTERNAL,
    },
  },
  {
    path: 'emissions/:pool',
    Component: lazy(
      () => import('../../pages/pages-behind-login/emissions/emissions'),
    ),
    handle: {
      restrictedTo: [
        new RegExp(/poolpartner:\w*:user$/),
        ExactScopeName.maerskTankersUser,
      ],
      disableLeftNavigation: true,
      pageAccess: PageAccess.EXTERNAL,
    },
    children: [
      {
        path: ':reportType',
        Component: lazy(
          () => import('../../pages/pages-behind-login/emissions/emissions'),
        ),
      },
    ],
  },
  {
    path: 'financials',
    Component: lazy(() => Promise.resolve({ default: () => <Outlet /> })),
    handle: {
      routeWithLeftNavigation: false,
      disableLeftNavigation: true,
      pageAccess: PageAccess.EXTERNAL,
    },
    children: [
      {
        index: true,
        Component: lazy(
          () => import('../../pages/pages-behind-login/financials/financials'),
        ),
      },
      {
        path: 'technical-management',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/technical-management/technical-management'),
        ),
        handle: {
          restrictedTo: [new RegExp(/vessel:[\w]*:technical$/)],
          disableLeftNavigation: true,
        },
      },
      {
        path: 'technical-management/:vessel',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/technical-management/bunker-adjustment/bunker-adjustment'),
        ),
        handle: {
          restrictedTo: [new RegExp(/vessel:[\w]*:technical$/)],
        },
        children: [
          {
            path: 'bunker-adjustment',
            Component: lazy(
              () =>
                import('../../pages/pages-behind-login/financials/vessel-financials/bunker-adjustment/bunker-adjustment'),
            ),
            children: [
              {
                index: true,
                element: <Navigate to="calculation" replace />,
              },
              {
                path: 'calculation',
                Component: lazy(
                  () =>
                    import('../../pages/pages-behind-login/financials/vessel-financials/bunker-adjustment/calculation/calculation'),
                ),
              },
              {
                path: 'trends',
                Component: lazy(
                  () =>
                    import('../../pages/pages-behind-login/financials/vessel-financials/bunker-adjustment/trends/trends'),
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'forecast/:pool',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/financials/forecast/forecast'),
        ),
        handle: {
          disableLeftNavigation: true,
          withPools: true,
          restrictedTo: [
            new RegExp(/poolpartner:\w*:user$/),
            ExactScopeName.maerskTankersUser,
          ],
        },
      },
      {
        path: 'forecast-inputs',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/financials/forecast/inputs'),
        ),
        handle: {
          pageAccess: PageAccess.INTERNAL,
          disableLeftNavigation: true,
          withPools: false,
          restrictedTo: [
            new RegExp(/pool:\w*:admin$/),
            ExactScopeName.poolAdmin,
          ],
        },
      },
      {
        path: 'earnings/:pool',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/financials/earnings/earnings'),
        ),
        handle: {
          disableLeftNavigation: true,
          withPools: true,
          restrictedTo: [
            new RegExp(/vessel:[\w]*:earnings$/),
            new RegExp(/poolpartner:[\w]*:user$/),
            ExactScopeName.maerskTankersUser,
          ],
        },
      },
      {
        path: 'compare-vessels/:pool',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/financials/compare-vessels/compare-vessels'),
        ),
        handle: {
          disableLeftNavigation: true,
          withPools: true,
          restrictedTo: [
            new RegExp(/poolpartner:\w*:user$/),
            ExactScopeName.maerskTankersUser,
          ],
        },
      },
      {
        path: 'average-distribution/:pool',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/financials/average-distribution'),
        ),
        handle: {
          disableLeftNavigation: true,
          withPools: true,
          restrictedTo: [
            new RegExp(/poolpartner:\w*:user$/),
            ExactScopeName.maerskTankersUser,
          ],
        },
      },
      {
        path: 'hire-payments/:pool',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/financials/hire-payments'),
        ),
        handle: {
          disableLeftNavigation: true,
          withPools: true,
          restrictedTo: [
            new RegExp(/poolpartner:\w*:user$/),
            ExactScopeName.maerskTankersUser,
          ],
        },
      },
      {
        path: 'poolpoints/:pool',
        Component: lazy(
          () => import('../../pages/pages-behind-login/financials/pool-points'),
        ),
        handle: {
          disableLeftNavigation: true,
          withPools: true,
          restrictedTo: [
            new RegExp(/poolpartner:\w*:user$/),
            ExactScopeName.maerskTankersUser,
          ],
        },
      },
      {
        path: 'poolpoints/:pool/voyage-summary-report',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/financials/voyage-summary-report'),
        ),
        handle: {
          disableLeftNavigation: true,
          withPools: true,
          restrictedTo: [
            new RegExp(/poolpartner:\w*:user$/),
            ExactScopeName.maerskTankersUser,
          ],
        },
      },
      {
        path: 'reports/:pool/:reportType',
        Component: lazy(
          () => import('../../pages/pages-behind-login/reports/report/report'),
        ),
        handle: {
          disableLeftNavigation: true,
          withPools: true,
          restrictedTo: [
            new RegExp(/poolpartner:\w*:user$/),
            ExactScopeName.maerskTankersUser,
          ],
        },
      },
      {
        path: 'reports/:pool',
        Component: lazy(
          () => import('../../pages/pages-behind-login/reports/reports'),
        ),
        handle: {
          disableLeftNavigation: true,
          withPools: true,
          restrictedTo: [
            new RegExp(/poolpartner:\w*:user$/),
            ExactScopeName.maerskTankersUser,
          ],
        },
      },
      {
        path: 'vessels/:vessel',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/financials/vessel-financials/vessel-financials'),
        ),
        handle: {
          restrictedTo: [
            new RegExp(/poolpartner:\w*:user$/),
            new RegExp(/vessel:\w*:earnings$/),
            ExactScopeName.maerskTankersUser,
          ],
          pageAccess: PageAccess.EXTERNAL,
        },
        children: [
          {
            path: 'earnings',
            Component: lazy(
              () =>
                import('../../pages/pages-behind-login/financials/vessel-financials/earnings/earnings'),
            ),
          },
          {
            path: 'payout',
            Component: lazy(
              () =>
                import('../../pages/pages-behind-login/financials/vessel-financials/payout/payout'),
            ),
          },
          {
            path: 'adjustments',
            Component: lazy(
              () =>
                import('../../pages/pages-behind-login/financials/vessel-financials/adjustments/adjustments'),
            ),
          },
          {
            path: 'pool-points',
            Component: lazy(
              () =>
                import('../../pages/pages-behind-login/financials/vessel-financials/pool-points/pool-points'),
            ),
          },
          {
            path: 'scrubber-bonus',
            Component: lazy(
              () =>
                import('../../pages/pages-behind-login/financials/vessel-financials/scrubber-bonus/scrubber-bonus'),
            ),
          },
          {
            path: 'bunker-adjustment',
            Component: lazy(
              () =>
                import('../../pages/pages-behind-login/financials/vessel-financials/bunker-adjustment/bunker-adjustment'),
            ),
            children: [
              {
                index: true,
                element: <Navigate to="calculation" replace />,
              },
              {
                path: 'calculation',
                Component: lazy(
                  () =>
                    import('../../pages/pages-behind-login/financials/vessel-financials/bunker-adjustment/calculation/calculation'),
                ),
              },
              {
                path: 'trends',
                Component: lazy(
                  () =>
                    import('../../pages/pages-behind-login/financials/vessel-financials/bunker-adjustment/trends/trends'),
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'contact',
    handle: {
      withPools: false,
      disableLeftNavigation: true,
      pageAccess: PageAccess.EXTERNAL,
    },
    children: [
      {
        index: true,
        Component: lazy(
          () => import('../../pages/pages-behind-login/contact/contact'),
        ),
      },
      {
        path: 'inputs',
        Component: lazy(
          () =>
            import('../../pages/pages-behind-login/contact-inputs/contact-inputs-page'),
        ),
        handle: {
          pageAccess: PageAccess.INTERNAL,
          restrictedTo: ['settings:contacts:write', 'settings:*:write'],
        },
      },
    ],
  },
  {
    path: '404',
    Component: lazy(() => import('../../pages/auth/unauthorized-page')),
    handle: { pageAccess: PageAccess.EXTERNAL },
  },
]
